import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Building2,
  Wallet,
  Users,
  Clock,
  Check,
  PauseCircle,
  X,
  Loader2,
  Inbox,
  ArrowRight,
} from "lucide-react";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { KpiCard } from "@/components/common/KpiCard";
import { GlassCard } from "@/components/common/GlassCard";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { KpiSkeletonGrid, ChartSkeleton } from "@/components/common/Skeletons";
import { PageTransition } from "@/components/common/PageTransition";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { usePageHeading } from "@/components/layout/AppLayout";
import { dashboardApi } from "@/lib/api";
import { formatCurrency, formatNumber, timeAgo } from "@/lib/format";
import { TID } from "@/constants/testIds";
import { ChartTooltip } from "@/components/common/ChartTooltip";

export default function Dashboard() {
  usePageHeading("Dashboard", "Overview & pending approvals");
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirm, setConfirm] = useState(null); // { action, clinic }

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([dashboardApi.summary(), dashboardApi.pending()]);
      const normalizedSummary = {
        ...s,
        mrr: s?.mrr ?? s?.totalRevenue ?? 0,
        totalSubscribers: s?.totalSubscribers ?? s?.activeClinics ?? 0,
        totalClinics: s?.totalClinics ?? 0,
        pendingApprovals: s?.pendingApprovals ?? s?.pendingVerification ?? 0,
        revenueDelta: s?.revenueDelta ?? 0,
        subscribersDelta: s?.subscribersDelta ?? 0,
        clinicsDelta: s?.clinicsDelta ?? 0,
        pendingDelta: s?.pendingDelta ?? 0,
        revenueTrend: s?.revenueTrend ?? [],
        planDistribution: s?.planDistribution ?? [],
      };
      const normalizedPending = (p || []).map((c) => ({
        ...c,
        id: c.id ?? c.clinicId ?? c.clinic?.id,
        name: c.clinicName ?? c.name ?? c.clinic?.clinicName ?? "Clinic",
        owner: c.owner ?? c.doctorName ?? c.contactPerson ?? "Owner",
        city: c.city ?? "",
        plan: c.planName ?? c.plan ?? c.subscriptionPlan ?? "Trial",
        status: (c.subscriptionStatus ?? c.status ?? "PENDING_APPROVAL").toLowerCase(),
        appliedAt: c.appliedAt ?? c.createdAt ?? new Date().toISOString(),
      }));
      setSummary(normalizedSummary);
      setPending(normalizedPending);
    } catch {
      toast.error("Failed to load dashboard", { description: "Could not reach the server." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const runAction = async () => {
    if (!confirm) return;
    const { action, clinic } = confirm;
    setBusyId(clinic.id);
    setConfirm(null);
    try {
      if (action === "approve") await dashboardApi.approve(clinic.id);
      if (action === "hold") await dashboardApi.hold(clinic.id);
      if (action === "reject") await dashboardApi.reject(clinic.id);
      const verb = { approve: "approved", hold: "placed on hold", reject: "rejected" }[action];
      toast.success(`Clinic ${verb}`, { description: clinic.name });
      if (action !== "hold") setPending((prev) => prev.filter((c) => c.id !== clinic.id));
      else setPending((prev) => prev.map((c) => (c.id === clinic.id ? { ...c, status: "hold" } : c)));
    } catch {
      toast.error("Action failed", { description: "Please try again." });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <PageTransition className="space-y-8" >
      <div data-testid={TID.dashboard} />
      {/* KPIs */}
      {loading || !summary ? (
        <KpiSkeletonGrid />
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard label="Monthly Revenue" value={formatCurrency(summary.mrr)} delta={summary.revenueDelta} icon={Wallet} accent="emerald" index={0} />
          <KpiCard label="Active Subscribers" value={formatNumber(summary.totalSubscribers)} delta={summary.subscribersDelta} icon={Users} accent="gold" index={1} />
          <KpiCard label="Total Clinics" value={formatNumber(summary.totalClinics)} delta={summary.clinicsDelta} icon={Building2} accent="indigo" index={2} />
          <KpiCard label="Pending Approvals" value={formatNumber(summary.pendingApprovals)} delta={summary.pendingDelta} icon={Clock} accent="emerald" index={3} />
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {loading || !summary ? (
          <>
            <ChartSkeleton className="lg:col-span-2" />
            <ChartSkeleton />
          </>
        ) : (
          <>
            <GlassCard className="p-6 lg:col-span-2">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h3 className="font-heading text-lg font-semibold tracking-tight">Revenue Trend</h3>
                  <p className="text-sm text-muted-foreground">Last 6 months</p>
                </div>
                <span className="rounded-full bg-lux-emerald/15 px-3 py-1 text-xs font-semibold text-lux-emerald">
                  +{summary.revenueDelta}%
                </span>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={summary.revenueTrend} margin={{ left: -12, right: 8, top: 8 }}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#50C878" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#50C878" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
                  <Tooltip content={<ChartTooltip formatter={(v) => formatCurrency(v)} />} />
                  <Area type="monotone" dataKey="revenue" stroke="#50C878" strokeWidth={2.5} fill="url(#revGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </GlassCard>

            <GlassCard className="p-6">
              <h3 className="mb-6 font-heading text-lg font-semibold tracking-tight">Plan Distribution</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={summary.planDistribution} dataKey="value" innerRadius={55} outerRadius={85} paddingAngle={4} stroke="none">
                    {summary.planDistribution.map((e) => (
                      <Cell key={e.name} fill={e.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip formatter={(v) => `${v}%`} />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-2">
                {summary.planDistribution.map((p) => (
                  <div key={p.name} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: p.color }} />
                      {p.name}
                    </span>
                    <span className="font-semibold">{p.value}%</span>
                  </div>
                ))}
              </div>
            </GlassCard>
          </>
        )}
      </div>

      {/* Pending approvals */}
      <div>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-heading text-xl font-semibold tracking-tight">Pending Applications</h2>
            <p className="text-sm text-muted-foreground">Clinic approvals are now handled from the dedicated Approvals page.</p>
          </div>
          <Button variant="secondary" className="text-muted-foreground hover:text-foreground" onClick={() => navigate("/approvals") }>
            Go to Approvals <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>

        {loading ? (
          <div className="grid gap-4 md:grid-cols-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="glass h-40 rounded-2xl" />
            ))}
          </div>
        ) : (
          <GlassCard className="p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Applications awaiting review</p>
                <p className="mt-3 text-3xl font-semibold text-foreground">{formatNumber(pending.length)}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => navigate("/approvals") }>
                Review applications
              </Button>
            </div>
          </GlassCard>
        )}
      </div>
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="glass border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading capitalize">{confirm?.action} clinic?</AlertDialogTitle>
            <AlertDialogDescription>
              You're about to <strong className="text-foreground">{confirm?.action}</strong> {confirm?.clinic?.name}. This will update its application status.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runAction}
              className="rounded-full bg-lux-emerald text-[#0a2417] hover:bg-lux-emeraldHover"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageTransition>
  );
}
